import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiHome, 
  FiUsers, 
  FiTruck, 
  FiSettings, 
  FiLogOut, 
  FiMenu, 
  FiX,
  FiShield,
  FiUser,
  FiMessageCircle,
  FiAtSign,
  FiBox,
  FiInfo,
  FiLayers,
  FiTool,
  FiFileText,
  FiPieChart,
  FiChevronDown,
  FiChevronRight,
  FiKey,
  FiShoppingCart,
  FiCreditCard,
  FiDollarSign,
  FiTarget,
  FiSend
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import theme from '../styles/theme';

const BOTTOM_REGION_HEIGHT = '84px';

// Componentes estilizados
const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${props => props.$isOpen ? '280px' : '0'};
  background: ${theme.colors.background.secondary};
  border-right: 1px solid ${theme.colors.border.primary};
  transition: width 0.3s ease;
  overflow: hidden;
  z-index: ${props => props.$isMobile && props.$isOpen ? theme.zIndex.modal + 1 : theme.zIndex.fixed};
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const LogoText = styled.h2`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.text.secondary};
    background: ${theme.colors.background.primary};
  }
`;

const UserInfo = styled.div`
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing[3]};
`;

const UserName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  margin-bottom: ${theme.spacing[1]};
`;

const UserRole = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${theme.spacing[4]} 0;
  overflow-y: auto;
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const NavItem = styled.li``;

const NavLink = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: none;
  border: none;
  color: ${props => props.$isActive ? theme.colors.primary[500] : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  text-align: left;
  border-radius: 0;
  position: relative;

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[400]};
  }

  ${props => props.$isActive && `
    background: ${theme.colors.primary[500]}20;
    border-right: 3px solid ${theme.colors.primary[500]};
  `}
`;

const NavIcon = styled.div`
  font-size: ${theme.fontSizes.lg};
  min-width: 20px;
`;

const NavText = styled.span`
  flex: 1;
`;

const SubmenuContainer = styled.div`
  overflow: hidden;
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  transition: max-height 0.3s ease;
`;

const SubmenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  background: ${theme.colors.background.primary};
`;

const SubmenuItem = styled.li``;

const SubmenuLink = styled.button`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[2]} ${theme.spacing[12]};
  background: none;
  border: none;
  color: ${props => props.$isActive ? theme.colors.primary[500] : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  text-align: left;
  border-radius: 0;
  position: relative;

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.primary[400]};
  }

  ${props => props.$isActive && `
    background: ${theme.colors.primary[500]}20;
    border-right: 3px solid ${theme.colors.primary[500]};
  `}
`;

const ChevronIcon = styled.div`
  font-size: ${theme.fontSizes.sm};
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'};
  margin-left: auto;
`;

const SidebarFooter = styled.div`
  height: ${BOTTOM_REGION_HEIGHT};
  padding: 0 ${theme.spacing[4]};
  box-shadow: inset 0 1px 0 ${theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: none;
  border: 1px solid ${theme.colors.error[500]};
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.base};
  font-weight: 500;
  cursor: pointer;
  border-radius: ${theme.borderRadius.lg};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.primary};
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.modal};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const MobileMenuButton = styled.button`
  position: fixed;
  top: ${theme.spacing[4]};
  left: ${theme.spacing[4]};
  z-index: ${theme.zIndex.modal + 10};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  transition: all 0.2s ease;
  display: none;
  min-width: 44px;
  min-height: 44px;
  box-shadow: ${theme.shadows.md};
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:hover {
    background: ${theme.colors.background.primary};
  }

  &:active {
    transform: scale(0.95);
    background: ${theme.colors.background.primary};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Itens de navegação baseados no tipo de usuário
const PERMISSION_LABELS = {
  GESTOR: 'Gestor',
  AVANCADO: 'Usuário Avançado',
  BASICO: 'Usuário Básico',
};

const getNavigationItems = (userType, permissionCode) => {
  const normalizedPermissao = permissionCode ? permissionCode.toUpperCase() : '';
  const itemsLog = {
    normalizedPermissao,
    isGestor: normalizedPermissao === 'GESTOR'
  };

  const dashboardSubmenu = {
    label: 'Dashboard',
    icon: FiPieChart,
    adminOnly: true,
      submenu: [
        {
          path: '/dashboard/configuracoes',
          label: 'Configurações',
          icon: FiSettings,
        },
        {
          path: '/dashboard/contratos',
          label: 'Contratos',
          icon: FiFileText,
        },
      ],
  };

  const whatsappSubmenu = {
    label: 'WhatsApp',
    icon: FiMessageCircle,
    adminOnly: false,
    submenu: [
      {
        path: '/whatsapp/conversas',
        label: 'Conversas',
        icon: FiMessageCircle,
      },
      {
        path: '/whatsapp/contatos',
        label: 'Contatos',
        icon: FiAtSign,
      },
    ],
  };

  const financeiroSubmenu = {
    label: 'Financeiro',
    icon: FiDollarSign,
    adminOnly: false,
    gestorOnly: true, // Apenas para gestores
    submenu: [
      {
        path: '/dashboard/meus-contratos',
        label: 'Contratos',
        icon: FiFileText,
      },
      {
        path: '/meus-cartoes',
        label: 'Cartões',
        icon: FiCreditCard,
      },
      {
        path: '/comprar-creditos',
        label: 'Comprar Créditos',
        icon: FiShoppingCart,
      },
    ],
  };

  const gestaoPlanosSubmenu = {
    label: 'Gestão de Planos',
    icon: FiLayers,
    adminOnly: true,
    submenu: [
      {
        path: '/gestao/contratos',
        label: 'Contratos',
        icon: FiFileText,
      },
      {
        path: '/gestao/planos',
        label: 'Planos',
        icon: FiLayers,
      },
      {
        path: '/gestao/recursos',
        label: 'Recursos',
        icon: FiTool,
      },
    ],
  };

  const administracaoSubmenu = {
    label: 'Administração',
    icon: FiSettings,
    adminOnly: true,
    submenu: [
      {
        path: '/administracao/keys-apis',
        label: 'API Keys',
        icon: FiKey,
      },
    ],
  };

  const baseItems = [
    {
      path: '/veiculos',
      icon: FiTruck,
      label: 'Veículos',
      adminOnly: false,
    },
    {
      path: '/clientes',
      icon: FiUsers,
      label: 'Clientes',
      adminOnly: true,
    },
    {
      path: '/cliente-sobre',
      icon: FiInfo,
      label: 'Info Revenda',
      adminOnly: false,
    },
    {
      path: '/dashboard/creditos',
      icon: FiPieChart,
      label: 'Dashboard de Créditos',
      adminOnly: false,
      clientOnly: true, // Apenas para usuários não-admin
    },
  ];

  const leadsSubmenu = {
    label: 'Leads',
    icon: FiTarget,
    adminOnly: true,
    submenu: [
      {
        path: '/leads',
        label: 'Lista de Leads',
        icon: FiUsers,
      },
      {
        path: '/leads/funis',
        label: 'Funis',
        icon: FiTarget,
      },
      {
        path: '/leads/mensagens-etapa',
        label: 'Mensagens de Etapa',
        icon: FiSend,
      },
    ],
  };

  const adminItems = [
    {
      path: '/usuarios',
      icon: FiUser,
      label: 'Usuários',
      adminOnly: true,
    },
    {
      path: '/administradores',
      icon: FiShield,
      label: 'Administradores',
      adminOnly: true,
    },
    leadsSubmenu,
  ];

  // Filtrar itens baseado no tipo de usuário
  if (userType === 'admin') {
    // Para admin, remover itens clientOnly
    const filteredBaseItems = baseItems.filter(item => !item.clientOnly);
    // Dashboard de Créditos não aparece para admin (é clientOnly), então não precisa ser movido
    return [dashboardSubmenu, whatsappSubmenu, ...filteredBaseItems, gestaoPlanosSubmenu, administracaoSubmenu, ...adminItems];
  }

  // Para usuários comuns, apenas rotas permitidas
  // Incluir itens que não são adminOnly OU que são clientOnly
  const baseItemsFiltered = baseItems.filter(item => !item.adminOnly || item.clientOnly);
  
  // Separar Dashboard de Créditos para colocá-lo primeiro
  const dashboardCreditosItem = baseItemsFiltered.find(item => item.path === '/dashboard/creditos');
  const otherBaseItems = baseItemsFiltered.filter(item => item.path !== '/dashboard/creditos');
  
  const permittedItems = dashboardCreditosItem 
    ? [dashboardCreditosItem, whatsappSubmenu, ...otherBaseItems]
    : [whatsappSubmenu, ...otherBaseItems];

  if (normalizedPermissao === 'GESTOR') {
    if (import.meta.env.DEV) {
      console.debug('[Sidebar] Permissão Gestor detectada, adicionando menu de usuários', itemsLog);
    }
    permittedItems.push({
      path: '/usuarios',
      icon: FiUser,
      label: 'Usuários',
      adminOnly: false,
    });
    // Adicionar submenu Financeiro para gestores
    permittedItems.push(financeiroSubmenu);
  }

  return permittedItems;
};

const Sidebar = ({ isOpen, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, getPermissionCode } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [openSubmenus, setOpenSubmenus] = useState({});

  // Detectar mudanças no tamanho da tela
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fechar sidebar em mobile quando navegar
  React.useEffect(() => {
    if (isMobile && isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Abrir submenu automaticamente se a rota ativa estiver dentro dele
  React.useEffect(() => {
    const permissionCode = getPermissionCode();
    const isUserAdmin = isAdmin();
    const items = getNavigationItems(user?.tipo, permissionCode);
    
    items.forEach(item => {
      if (item.submenu) {
        // Verificar se alguma rota do submenu corresponde à rota atual ou começa com ela
        const isActive = item.submenu.some(subItem => {
          const currentPath = location.pathname;
          const subItemPath = subItem.path;
          return currentPath === subItemPath || currentPath.startsWith(`${subItemPath}/`);
        });
        if (isActive) {
          setOpenSubmenus(prev => ({
            ...prev,
            [item.label]: true
          }));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.tipo]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const isSubmenuItemActive = (submenuItems) => {
    return submenuItems.some(item => {
      const currentPath = location.pathname;
      const itemPath = item.path;
      return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const permissionCode = getPermissionCode();
  const normalizedPermissao = permissionCode ? permissionCode.toUpperCase() : '';
  const isUserAdmin = isAdmin();
  const navigationItems = getNavigationItems(user?.tipo, permissionCode);
  const permissionLabel = PERMISSION_LABELS[permissionCode] || 'Usuário';

  return (
    <>
      {/* Botão de menu mobile */}
      {isMobile && (
        <MobileMenuButton 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }}
          type="button"
          aria-label="Abrir menu"
        >
          <FiMenu />
        </MobileMenuButton>
      )}

      {/* Overlay mobile */}
      {isMobile && <MobileOverlay $isOpen={isOpen} onClick={onToggle} />}

      {/* Sidebar */}
      <SidebarContainer $isOpen={isOpen || !isMobile} $isMobile={isMobile}>

        <UserInfo>
          <UserAvatar>
            {isUserAdmin || user?.permissao === 'GESTOR' ? <FiShield /> : <FiUser />}
          </UserAvatar>
          <UserName>{user?.nome}</UserName>
          <UserRole>
            {isUserAdmin ? (
              <>
                <FiShield />
                Administrador
              </>
            ) : (
              <>
                <FiUser />
                {permissionLabel}
              </>
            )}
          </UserRole>
        </UserInfo>

        <Navigation>
          <NavList>
            {navigationItems.map((item) => {
              // Verificar se item tem gestorOnly e se o usuário é gestor
              if (item.gestorOnly && normalizedPermissao !== 'GESTOR') {
                return null;
              }

              if (item.submenu) {
                const isSubmenuOpen = openSubmenus[item.label] || false;
                const isSubmenuActive = isSubmenuItemActive(item.submenu);
                
                return (
                  <NavItem key={item.label}>
                    <NavLink
                      $isActive={isSubmenuActive}
                      onClick={() => toggleSubmenu(item.label)}
                    >
                      <NavIcon>
                        <item.icon />
                      </NavIcon>
                      <NavText>{item.label}</NavText>
                      <ChevronIcon $isOpen={isSubmenuOpen}>
                        <FiChevronDown />
                      </ChevronIcon>
                    </NavLink>
                    <SubmenuContainer $isOpen={isSubmenuOpen}>
                      <SubmenuList>
                        {item.submenu.map((subItem) => (
                          <SubmenuItem key={subItem.path}>
                            <SubmenuLink
                              $isActive={location.pathname === subItem.path || location.pathname.startsWith(`${subItem.path}/`)}
                              onClick={() => handleNavigation(subItem.path)}
                            >
                              <NavIcon>
                                <subItem.icon />
                              </NavIcon>
                              <NavText>{subItem.label}</NavText>
                            </SubmenuLink>
                          </SubmenuItem>
                        ))}
                      </SubmenuList>
                    </SubmenuContainer>
                  </NavItem>
                );
              }
              
              return (
                <NavItem key={item.path}>
                  <NavLink
                    $isActive={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                  >
                    <NavIcon>
                      <item.icon />
                    </NavIcon>
                    <NavText>{item.label}</NavText>
                  </NavLink>
                </NavItem>
              );
            })}
          </NavList>
        </Navigation>

        <SidebarFooter>
          <LogoutButton onClick={handleLogout}>
            <FiLogOut />
            Sair
          </LogoutButton>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
